import { computed, ref, Ref } from 'vue';
import { ComplexModal } from '@studyportals/modal';
import { ModalCustomProductStates } from '@/models/modal/modal.enum';
import { CustomProductDto, CustomProductTypes, CustomProductValidator } from '@studyportals/campaign-management-api-interface';
import { EmitOptions } from '@/models/emit-options.enum';
import { IUnsavedCustomProductData } from '@/models/custom-products.interface';
import { IMultiSelect } from '@studyportals/multiselect/dist/src/interfaces/multiselect.interface';
import { IOption } from '@studyportals/multiselect/dist/src/interfaces/options.interface';
import customProductPresenter from '@/presenters/custom-product-presenter';
import datePresenter from'@/presenters/date-presenter';
import store from '@/store';

export default class CustomProductModalCode {
	public modalContent: Ref<HTMLElement | null> = ref(null);
	public typeDropdown: Ref<IMultiSelect | null> = ref(null);
	public productType: Ref<CustomProductTypes | null> = ref(null);
	public productPrice: Ref<number | null> = ref(null);
	public productDate: Ref<Date | null> = ref(null);
	public productName: Ref<string> = ref('');
	public productDescription: Ref<string> = ref('');
	private modal: Ref<ComplexModal | undefined> = ref(undefined);

	public get customProductOptions(): IOption[] {
		return Object.values(CustomProductTypes)
			.filter((type: CustomProductTypes) => type !== CustomProductTypes.FREE_VALUE)
			.map((type: CustomProductTypes) => {
				return {
					value: type,
					label: customProductPresenter.retrieveLabelOfType(type)
				};
			});
	}

	public modalTitle = computed((): string => {
		switch (this.modalCustomProductState.value) {
		case ModalCustomProductStates.ADD_PRODUCT:
			return `Add custom product: ${this.productTypeBasedModalTitlePart.value}`;
		case ModalCustomProductStates.DUPLICATE_PRODUCT:
			return `Duplicate custom product: ${this.productTypeBasedModalTitlePart.value}`;
		case ModalCustomProductStates.EDIT_PRODUCT:
			return 'Edit custom product';
		case ModalCustomProductStates.DELETE_PRODUCT:
			return 'Delete custom product';
		default:
			throw Error(`CustomProductModal.modalTitle: invalid modal custom product state: ${this.modalCustomProductState.value}`);
		}
	});

	public customProductType = computed((): CustomProductTypes | null => {
		if (!this.selectedCustomProduct.value) {
			return this.selectedTypeForCustomProductCreation.value;
		}

		return this.selectedCustomProduct.value.productType;
	});

	public selectedCustomProduct = computed((): IUnsavedCustomProductData | CustomProductDto | undefined => {
		const selectedCustomProductId = store.getters.selectedCustomProductId();
		const customProducts = store.getters.customProducts();
		return customProducts.find((product) => product.id === selectedCustomProductId);
	});

	public productDateAsDateString = computed((): string | undefined => {
		if (!this.productDate.value) {
			return;
		}

		return datePresenter.transformDateObjectToUTCDateString(this.productDate.value);
	});

	public modalStateIsAddProduct = computed((): boolean => {
		return this.modalCustomProductState.value === ModalCustomProductStates.ADD_PRODUCT;
	});

	public modalStateIsDuplicateProduct = computed((): boolean => {
		return this.modalCustomProductState.value === ModalCustomProductStates.DUPLICATE_PRODUCT;
	});

	public modalStateIsEditProduct = computed((): boolean => {
		return this.modalCustomProductState.value === ModalCustomProductStates.EDIT_PRODUCT;
	});

	public modalStateIsDeleteProduct = computed((): boolean => {
		return this.modalCustomProductState.value === ModalCustomProductStates.DELETE_PRODUCT;
	});

	private modalCustomProductState = computed((): ModalCustomProductStates => {
		return store.getters.modalCustomProductState();
	});

	private selectedTypeForCustomProductCreation = computed((): CustomProductTypes | null => {
		return store.getters.selectedTypeForCustomProductCreation();
	});

	private startDate = computed((): Date | null => {
		return store.getters.startDate();
	});

	private endDate = computed((): Date | null => {
		return store.getters.endDate();
	});

	public isDeliveryDateFieldValidatedAndInvalid = computed((): boolean => {
		if (!this.productDate.value ) {
			return false;
		}

		if (!this.startDate.value || !this.endDate.value) {
			return true;
		}

		return !CustomProductValidator.validateDeliveryDate(
			this.startDate.value,
			this.endDate.value,
			this.productDate.value
		);
	});

	private productTypeBasedModalTitlePart = computed((): string => {
		if (!this.customProductType.value) {
			return '';
		}

		return customProductPresenter.retrieveLabelOfType(this.customProductType.value);
	});

	constructor(
		private emit: (event: string) => void
	) { }

	public mounted(): void {
		this.prepareModal();
		this.markDatePickerAsRequired();
		this.prefillFields();
		this.prefillTypeDropdown();
	}

	public unmounted(): void {
		window.ModalManager.close(this.modal.value);
	}

	public closeModal(): void {
		this.emit(EmitOptions.CLOSE_MODAL);
		store.mutations.setSelectedCustomProductId(null);
		store.mutations.setModalCustomProductState(ModalCustomProductStates.INACTIVE);
	}

	public updateProductType(option: IOption): void {
		if (this.customProductOptions.find(
			(customProductOption) => customProductOption.value === option.value
		) === undefined) {
			return;
		}

		this.productType.value = option.value as CustomProductTypes;
	}

	public setDate(event: { target: { value: string | null } }): void {
		if (!event.target?.value) {
			return;
		}

		this.productDate.value = datePresenter.transformUTCDateStringToDateObject(event.target.value);
	}

	public setDescription(event: { target: { value: string | null } }): void {
		if (!event.target?.value) {
			return;
		}

		this.productDescription.value = event.target.value;
	}

	public onSubmit(): void {
		switch (this.modalCustomProductState.value) {
		case ModalCustomProductStates.ADD_PRODUCT:
		case ModalCustomProductStates.DUPLICATE_PRODUCT:
			this.addCustomProduct(false);
			break;
		case ModalCustomProductStates.EDIT_PRODUCT:
			this.editCustomProduct();
			break;
		case ModalCustomProductStates.DELETE_PRODUCT:
			this.deleteCustomProduct();
			break;
		default:
			throw Error(`CustomProductModal.onSubmit: invalid modal custom product state: ${this.modalCustomProductState.value}`);
		}
	}

	public addCustomProduct(wasDuplicateSelected: boolean): void {
		if (this.productPrice.value === null ||
			!this.productDate.value ||
			!this.selectedTypeForCustomProductCreation.value ||
			this.isDeliveryDateFieldValidatedAndInvalid.value) {
			return;
		}

		const id = customProductPresenter.newCustomProductLabel.value;

		store.mutations.addCustomProduct({
			id,
			productType: this.selectedTypeForCustomProductCreation.value,
			name: this.productName.value,
			description: this.productDescription.value,
			price: this.productPrice.value,
			date: this.productDate.value
		});

		store.mutations.addIdOfModifiedCustomProduct(id);

		if (wasDuplicateSelected) {
			store.mutations.setModalCustomProductState(ModalCustomProductStates.DUPLICATE_PRODUCT);
			return;
		}

		this.closeModal();
	}

	private editCustomProduct(): void {
		if (this.productPrice.value === null ||
			!this.productDate.value ||
			!this.selectedCustomProduct.value ||
			!this.productType.value ||
			this.isDeliveryDateFieldValidatedAndInvalid.value) {
			return;
		}

		const customProduct = this.selectedCustomProduct.value;
		customProduct.productType = this.productType.value;
		customProduct.name = this.productName.value;
		customProduct.description = this.productDescription.value;
		customProduct.price = this.productPrice.value;
		customProduct.date = this.productDate.value;

		store.mutations.editCustomProduct(customProduct);
		store.mutations.addIdOfModifiedCustomProduct(customProduct.id);
		this.closeModal();
	}

	private deleteCustomProduct(): void {
		if (!this.selectedCustomProduct.value) {
			return;
		}

		store.mutations.deleteCustomProduct(this.selectedCustomProduct.value.id);
		this.closeModal();
	}

	private prepareModal(): void {
		if (!this.modalContent.value) {
			return;
		}

		this.modal.value = new ComplexModal(this.modalContent.value, {
			cssClassName: 'CustomProductModalWrapper',
			destroyOnClose: false,
			onClose: this.closeModal.bind(this)
		});

		window.ModalManager.open(this.modal.value);
	}

	private prefillFields(): void {
		if (!this.selectedCustomProduct.value) {
			return;
		}

		this.productType.value = this.selectedCustomProduct.value.productType;
		this.productName.value = this.selectedCustomProduct.value.name;
		this.productDescription.value = this.selectedCustomProduct.value.description;
		this.productPrice.value = this.selectedCustomProduct.value.price;
		this.productDate.value = this.selectedCustomProduct.value.date;
	}

	private prefillTypeDropdown(): void {
		if (!this.typeDropdown.value) {
			return;
		}

		const selectedOption = this.customProductOptions.find(
			(option) => option.value === this.selectedCustomProduct.value?.productType
		);
		if (!selectedOption) {
			return;
		}

		this.typeDropdown.value.selectOption(selectedOption);
	}

	private markDatePickerAsRequired(): void {
		// The Element-plus documentation does not mention a way to set the required attribute on the date picker.
		const element: HTMLInputElement | null = document.querySelector('.el-input__inner');
		if (!element) {
			return;
		}

		element.required = true;
	}
}
